import { Badge, Box, Divider, Heading, HStack, Skeleton, Stack, Text, VStack } from '@chakra-ui/react';
import { useListClientContracts, useListContracts } from 'hooks/useApiQueries';
import { ClientContract, UpcomingAutopayEvent } from 'types/clientContract';
import { MindbodyContract } from 'types/contract';
import { getBrandColor } from 'utils/brandColor';

const primaryColor = getBrandColor();

function formatDateFR(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatAmount(value: number | null | undefined): string {
  if (value == null) return '—';
  const formatted = value.toFixed(2).replace(/\.00$/, '').replace('.', ',');
  return `${formatted}€`;
}

function statusBadge(status: string): { label: string; colorScheme: string } {
  switch (status) {
    case 'Active':
      return { label: 'Actif', colorScheme: 'green' };
    case 'Suspended':
    case 'Paused':
      return { label: 'En pause', colorScheme: 'orange' };
    case 'Terminated':
      return { label: 'Résilié', colorScheme: 'red' };
    case 'Inactive':
    default:
      return { label: 'Inactif', colorScheme: 'gray' };
  }
}

function countRemainingEvents(events: UpcomingAutopayEvent[] | null | undefined): number {
  if (!events) return 0;
  return events.length;
}

function nextEventDate(events: UpcomingAutopayEvent[] | null | undefined): string | null {
  if (!events || events.length === 0) return null;
  const first = events[0];
  const date = (first.ScheduleDate as string | undefined) ?? null;
  return date;
}

/**
 * Choisit le montant + label à afficher pour un contract.
 * - `NormalAutoPay > 0` : prélèvement récurrent normal (cas nominal).
 * - sinon `RecurringPaymentAmountTotal > 0` (catalogue) : Mindbody n'a pas
 *   encore populé l'autopay sur l'instance, on retombe sur le tarif catalogue.
 * - sinon `FirstPaymentAmountTotal` (catalogue) : contrat sans récurrent
 *   (paiement unique à la souscription).
 */
function resolveContractAmount(
  clientContract: ClientContract,
  template: MindbodyContract | undefined,
): { label: string; amount: number | null } {
  if (clientContract.NormalAutoPay && clientContract.NormalAutoPay > 0) {
    return { label: 'Prélèvement mensuel', amount: clientContract.NormalAutoPay };
  }
  if (template?.RecurringPaymentAmountTotal && template.RecurringPaymentAmountTotal > 0) {
    return { label: 'Prélèvement mensuel', amount: template.RecurringPaymentAmountTotal };
  }
  if (template?.FirstPaymentAmountTotal && template.FirstPaymentAmountTotal > 0) {
    return { label: 'Paiement unique', amount: template.FirstPaymentAmountTotal };
  }
  // Dernier recours : ce que retourne Mindbody, même si 0.
  return { label: 'Prélèvement mensuel', amount: clientContract.NormalAutoPay ?? clientContract.FirstAutoPay ?? null };
}

function ContractRow({ contract, template }: { contract: ClientContract; template: MindbodyContract | undefined }) {
  const badge = statusBadge(contract.AutopayStatus);
  const nextDate = nextEventDate(contract.UpcomingAutopayEvents);
  const remaining = countRemainingEvents(contract.UpcomingAutopayEvents);
  const { label: amountLabel, amount } = resolveContractAmount(contract, template);

  return (
    <Stack spacing={3} py={4} borderBottom="1px solid" borderColor="blackAlpha.100" _last={{ borderBottom: 'none' }}>
      <HStack justify="space-between" align="flex-start" spacing={3}>
        <Text fontWeight="700" fontSize="md" textTransform="uppercase" color="#1a1a1a">
          {contract.ContractName}
        </Text>
        <Badge colorScheme={badge.colorScheme} variant="subtle" px={2} py={0.5} borderRadius="full" textTransform="uppercase">
          {badge.label}
        </Badge>
      </HStack>

      <HStack spacing={6} flexWrap="wrap">
        <VStack align="flex-start" spacing={0}>
          <Text fontSize="xs" textTransform="uppercase" color="gray.500" letterSpacing="wide">
            {amountLabel}
          </Text>
          <Text fontSize="xl" fontWeight="800" color={primaryColor} lineHeight="1.2">
            {formatAmount(amount)}
          </Text>
        </VStack>

        <VStack align="flex-start" spacing={0}>
          <Text fontSize="xs" textTransform="uppercase" color="gray.500" letterSpacing="wide">
            Date de début
          </Text>
          <Text fontSize="sm" fontWeight="600" color="#1a1a1a">
            {formatDateFR(contract.StartDate)}
          </Text>
        </VStack>
      </HStack>

      <Divider />

      <Stack spacing={2} fontSize="sm" color="#4a4a4a">
        <HStack justify="space-between">
          <Text>Prochain prélèvement</Text>
          <Text fontWeight="600" color="#1a1a1a">
            {nextDate ? formatDateFR(nextDate) : '—'}
          </Text>
        </HStack>

        {contract.MinimumCommitmentEndDate && (
          <HStack justify="space-between">
            <Text>Fin d'engagement</Text>
            <Text fontWeight="600" color="#1a1a1a">
              {formatDateFR(contract.MinimumCommitmentEndDate)}
            </Text>
          </HStack>
        )}

        {remaining > 0 && (
          <HStack justify="space-between">
            <Text>Échéances restantes</Text>
            <Text fontWeight="600" color="#1a1a1a">
              {remaining}
            </Text>
          </HStack>
        )}
      </Stack>
    </Stack>
  );
}

export function MySubscription() {
  const { data: contracts, isLoading } = useListClientContracts();
  // Catalogue Mindbody pour fallback du montant quand l'instance ne l'expose
  // pas (cf. resolveContractAmount). Pas bloquant : si la requête tarde, on
  // affiche le client-contract avec ce qu'on a.
  const { data: catalogue } = useListContracts();

  const activeContracts = (contracts ?? []).filter((c) => c.AutopayStatus === 'Active');

  if (!isLoading && activeContracts.length === 0) return null;

  return (
    <Box id="mon-abonnement" className="accountBox" w="full">
      <Heading className="accountSubTitle" size="md" sx={{ padding: '1em' }}>
        MON ABONNEMENT
      </Heading>

      <Skeleton isLoaded={!isLoading}>
        <Box px="1em" pb="1em">
          {activeContracts.map((contract) => (
            <ContractRow
              key={contract.Id}
              contract={contract}
              template={catalogue?.find((c) => c.Id === contract.ContractID)}
            />
          ))}
        </Box>
      </Skeleton>
    </Box>
  );
}

export default MySubscription;
